import { EventBus } from "../EventBus.js";
import { PartyStore } from "../../store/PartyStore.js";
import {
  PATCHES,
  startAmbientPatch,
  type AmbientHandle,
  type AmbientMood,
  type SoundId,
} from "./synthPatches.js";

/**
 * Game audio (GF3) — module singleton over Web Audio.
 *
 * All sounds are synthesized (see synthPatches.ts); there are no audio
 * assets to load, so this always works and never blocks. The manager:
 *
 *   - lazily creates the AudioContext on `unlock()` (browsers require a
 *     user gesture before audio may start — main.tsx calls unlock from
 *     its first pointerdown/keydown);
 *   - exposes `play(id)` for explicit cues (footsteps from the input
 *     path) and self-subscribes to EventBus for everything combat/world
 *     shaped, so gameplay code mostly doesn't know audio exists;
 *   - runs one continuous ambient bed per map "mood", crossfaded on map
 *     change;
 *   - throttles the unseen-hostile threat growl so proximity reads as
 *     occasional dread, not a metal detector;
 *   - persists master/sfx/ambient volumes to localStorage.
 *
 * Environments without AudioContext (jsdom tests) disable the whole
 * module — every call is a safe no-op.
 */

const VOLUME_KEYS = {
  master: "bk:vol:master",
  sfx: "bk:vol:sfx",
  ambient: "bk:vol:ambient",
} as const;

export type VolumeChannel = keyof typeof VOLUME_KEYS;

const VOLUME_DEFAULTS: Record<VolumeChannel, number> = {
  master: 0.8,
  sfx: 0.9,
  ambient: 0.55,
};

const GROWL_COOLDOWN_S = 5;
const AMBIENT_CROSSFADE_S = 1.8;

export function clampVolume(v: number): number {
  return Math.min(1, Math.max(0, Number.isFinite(v) ? v : 0));
}

function loadVolume(channel: VolumeChannel): number {
  try {
    const raw = localStorage.getItem(VOLUME_KEYS[channel]);
    if (raw === null) return VOLUME_DEFAULTS[channel];
    return clampVolume(Number.parseFloat(raw));
  } catch {
    return VOLUME_DEFAULTS[channel];
  }
}

class AudioManager {
  private ctx: AudioContext | null = null;
  private masterGain: GainNode | null = null;
  private sfxGain: GainNode | null = null;
  private ambientGain: GainNode | null = null;
  private readonly volumes: Record<VolumeChannel, number> = {
    master: loadVolume("master"),
    sfx: loadVolume("sfx"),
    ambient: loadVolume("ambient"),
  };
  private ambient: AmbientHandle | null = null;
  private ambientMood: AmbientMood | null = null;
  private pendingMood: AmbientMood | null = null;
  private lastGrowlAt = -Infinity;
  private readonly supported = typeof AudioContext !== "undefined";
  private subscribed = false;

  /**
   * Create (or resume) the context. Safe to call repeatedly; call from a
   * user-gesture handler at least once or the browser keeps us muted.
   */
  unlock(): void {
    if (!this.supported) return;
    if (!this.ctx) {
      this.ctx = new AudioContext();
      this.masterGain = this.ctx.createGain();
      this.masterGain.gain.value = this.volumes.master;
      this.masterGain.connect(this.ctx.destination);
      this.sfxGain = this.ctx.createGain();
      this.sfxGain.gain.value = this.volumes.sfx;
      this.sfxGain.connect(this.masterGain);
      this.ambientGain = this.ctx.createGain();
      this.ambientGain.gain.value = this.volumes.ambient;
      this.ambientGain.connect(this.masterGain);
      this.subscribeOnce();
      // A mood may have been requested before the first gesture.
      if (this.pendingMood) {
        const mood = this.pendingMood;
        this.pendingMood = null;
        this.setAmbient(mood);
      }
    }
    if (this.ctx.state === "suspended") void this.ctx.resume();
  }

  /** True once the context exists (i.e. a user gesture has landed). */
  isRunning(): boolean {
    return this.ctx !== null && this.ctx.state === "running";
  }

  play(id: SoundId): void {
    if (!this.ctx || !this.sfxGain) return;
    if (this.ctx.state !== "running") return;
    try {
      PATCHES[id](this.ctx, this.sfxGain, this.ctx.currentTime);
    } catch {
      // A torn-down context mid-navigation is not worth crashing over.
    }
  }

  /**
   * Switch the continuous ambient bed. No-op when the mood is unchanged;
   * crossfades otherwise. Callable before unlock — the mood is remembered
   * and started on the first gesture.
   */
  setAmbient(mood: AmbientMood): void {
    if (!this.supported) return;
    if (!this.ctx || !this.ambientGain) {
      this.pendingMood = mood;
      return;
    }
    if (this.ambientMood === mood && this.ambient) return;
    this.ambient?.stop(AMBIENT_CROSSFADE_S);
    this.ambient = startAmbientPatch(this.ctx, this.ambientGain, mood);
    this.ambientMood = mood;
  }

  /**
   * Called from the step path with the Chebyshev distance to the nearest
   * living unseen hostile. Close-but-not-engaged plays an occasional
   * low growl, louder the closer they are.
   */
  threatNear(distance: number): void {
    if (!this.ctx || !this.sfxGain) return;
    if (this.ctx.state !== "running") return;
    const now = this.ctx.currentTime;
    if (now - this.lastGrowlAt < GROWL_COOLDOWN_S) return;
    this.lastGrowlAt = now;
    const g = this.ctx.createGain();
    g.gain.value = Math.min(1, 1.6 / Math.max(1, distance));
    g.connect(this.sfxGain);
    try {
      PATCHES.growl(this.ctx, g, now);
    } catch {
      // see play()
    }
  }

  getVolume(channel: VolumeChannel): number {
    return this.volumes[channel];
  }

  setVolume(channel: VolumeChannel, value: number): void {
    const v = clampVolume(value);
    this.volumes[channel] = v;
    try {
      localStorage.setItem(VOLUME_KEYS[channel], String(v));
    } catch {
      // Private-mode storage failures shouldn't kill the slider.
    }
    const node =
      channel === "master" ? this.masterGain : channel === "sfx" ? this.sfxGain : this.ambientGain;
    if (node && this.ctx) node.gain.setTargetAtTime(v, this.ctx.currentTime, 0.05);
  }

  /** EventBus-driven cues — gameplay code never calls audio directly for these. */
  private subscribeOnce(): void {
    if (this.subscribed) return;
    this.subscribed = true;
    EventBus.on("combat:attack", ({ hit }) => this.play(hit ? "attack_hit" : "attack_miss"));
    // combat:damage_taken fires for BOTH sides — the hurt sting is only
    // for party members (enemy impacts are covered by the attack sounds).
    EventBus.on("combat:damage_taken", ({ instanceId }) => {
      if (PartyStore.getMember(instanceId)) this.play("player_hurt");
    });
    EventBus.on("enemy:died", () => this.play("enemy_died"));
    EventBus.on("world:trap_triggered", () => this.play("trap_fire"));
    EventBus.on("world:trap_disarmed", () => this.play("trap_disarmed"));
    EventBus.on("world:door_state_changed", () => this.play("door_open"));
    EventBus.on("combat:healed", () => this.play("heal"));
    EventBus.on("world:level_up", () => this.play("level_up"));
    EventBus.on("world:long_rest_completed", () => this.play("camp_rest"));
    EventBus.on("world:loot_dropped", () => this.play("loot_drop"));
  }
}

export const audio = new AudioManager();
